import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DataRoomQuestionAnswererMembers,
  Question,
  QuestionFilterCount,
  QuestionPriority,
} from '../../models/QAndA';
import QAndAContext from './QAndA.context';
import CircledArrowUpIcon from 'icons/CircledArrowUp.icon';
import CircledDashIcon from 'icons/CircledDash.icon';
import CircledArrowDownIcon from 'icons/CircledArrowDown.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { QUESTION_QUERY_ARRAYS } from '../../components/QAndARoleFilter/data';
import { useTranslation } from 'react-i18next';
import dataRoomApi from '../../dataRoomApi';
import { DataRoomFaqAccessGranularityData } from '../../models/FAQ';
import {
  hasUserFaqPermission,
  isQARoleNone,
  isQASellExpert,
  isBuyManageRole,
} from '../../utils';
import DataRoomContext from '../DataRoom/DataRoom.context';

export type QAndAProviderProps = {
  children?: React.ReactNode;
  displayedQuestions: Question[];
  filterCount: QuestionFilterCount;
  refetch: () => void;
  setDisplayedQuestions: React.Dispatch<React.SetStateAction<Question[]>>;
};

export const PRIORITY_ICONS: Record<QuestionPriority, React.ReactNode> = {
  High: <CircledArrowUpIcon color="red" />,
  Medium: <CircledDashIcon />,
  Low: <CircledArrowDownIcon color="green" />,
};

const QAndAProvider: React.FC<QAndAProviderProps> = (props) => {
  const {
    children,
    displayedQuestions,
    filterCount,
    refetch,
    setDisplayedQuestions,
  } = props;

  const {
    isBuySide,
    qaRole,
    dataRoom: {
      _id: dataRoomId,
      currentMember: { _id: currentMemberId },
    },
  } = useContext(DataRoomContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const [members, setMembers] = useState<DataRoomQuestionAnswererMembers>();
  const [isFaqAccessLoading, setIsFaqAccessLoading] = useState(true);
  const [faqAccessData, setFaqAccessData] =
    useState<DataRoomFaqAccessGranularityData>(null);

  const canFaq = useMemo(() => hasUserFaqPermission(qaRole), [qaRole]);

  // assume that fetched questions are only for my buy side group
  const isDeletionAllowedForBuyer = useMemo(
    () => isBuyManageRole(qaRole),
    [qaRole],
  );

  const isUsersQuestion = useCallback(
    (question: Question) => currentMemberId === question.user._id,
    [currentMemberId],
  );

  const canDeleteQuestionBasedOnStatus = useCallback(
    ({ status }: Question) =>
      [
        ...QUESTION_QUERY_ARRAYS.IN_REVIEW_BUY_SIDE,
        ...QUESTION_QUERY_ARRAYS.AWAITING_ANSWER_BUY_SIDE,
      ].includes(status),
    [],
  );

  const isDeletionAllowedForSeller = useCallback(
    (question: Question) =>
      isUsersQuestion(question) &&
      isQASellExpert(qaRole) &&
      canDeleteQuestionBasedOnStatus(question),
    [qaRole, isUsersQuestion, canDeleteQuestionBasedOnStatus],
  );

  const isQuestionDeletableForUser = useCallback(
    (question: Question) =>
      isDeletionAllowedForBuyer || isDeletionAllowedForSeller(question),
    [isDeletionAllowedForBuyer, isDeletionAllowedForSeller],
  );

  const deleteQuestionCallback = useCallback(
    async (question: Question) => {
      try {
        await dataRoomApi.questionsAndAnswers.deleteQuestion(question._id);
        popSuccess(t('successfullyDeleted', { qSubject: question.subject }));
      } catch (e) {
        popServerError(e);
      }
    },
    [popServerError, popSuccess, t],
  );

  const updateQuestion = useCallback(
    (question: Partial<Question>) =>
      setDisplayedQuestions((old) =>
        old.map((el) =>
          el._id === question._id ? { ...el, ...question } : el,
        ),
      ),
    [setDisplayedQuestions],
  );

  useEffect(() => {
    if (isBuySide || isQARoleNone(qaRole)) return;

    (async () => {
      try {
        const { data } =
          await dataRoomApi.questionsAndAnswers.getDataRoomQuestionAnswererMembers(
            dataRoomId,
          );
        setMembers(data);
      } catch (e) {
        popServerError(e);
      }
    })();
  }, [dataRoomId, isBuySide, qaRole, popServerError]);

  useEffect(() => {
    (async () => {
      try {
        if (!canFaq) return;
        setIsFaqAccessLoading(true);

        const { data } = await dataRoomApi.faqs.getFaqsAccessData(dataRoomId);
        setFaqAccessData(data);
      } catch (e) {
        return;
      } finally {
        setIsFaqAccessLoading(false);
      }
    })();
  }, [canFaq, dataRoomId]);

  return (
    <QAndAContext.Provider
      value={{
        displayedQuestions,
        canFaq,
        filterCount,
        isFaqAccessLoading,
        faqAccessData,
        questionAnswererMembers: members,
        isQuestionDeletableForUser,
        deleteQuestionCallback,
        setDisplayedQuestions,
        refetch,
        updateQuestion,
      }}
    >
      {children}
    </QAndAContext.Provider>
  );
};

export default QAndAProvider;
